import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  FormControl,
  FormLabel,
  Grid,
  Input,
  Textarea,
  LinearProgress,
  Modal,
  ModalClose,
  Option,
  Select,
  Sheet,
  Stack,
  ModalDialog,
  Table,
  TextField,
  Typography,
} from '@mui/joy';
import { useSnackbar } from 'notistack';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  getServiceTypes,
  createService,
  updateService,
  deleteService,
  listBatchServices,
  fetchAllAdminMembers,
} from '../../api';
import {
  CongregationService,
  CongregationServiceStatus,
  CongregationServiceType,
  CongregationServiceInput,
  CongregationMemberSearchResult,
} from '../../types/congregation';

export function AdminServices() {
  const { enqueueSnackbar } = useSnackbar();
  const [services, setServices] = useState<CongregationService[]>([]);
  const [serviceTypes, setServiceTypes] = useState<CongregationServiceType[]>([]);
  const [members, setMembers] = useState<CongregationMemberSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [statusFilter, setStatusFilter] = useState<CongregationServiceStatus | 'ALL'>('ACTIVE');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingService, setEditingService] = useState<CongregationService | null>(null);

  // Form state
  const [selectedMember, setSelectedMember] = useState('');
  const [selectedServiceType, setSelectedServiceType] = useState('');
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs());
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [editingStatus, setEditingStatus] = useState<CongregationServiceStatus>('ACTIVE');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [typesRes, membersRes] = await Promise.all([
        getServiceTypes(),
        fetchAllAdminMembers(),
      ]);
      setServiceTypes(typesRes.serviceTypes || []);
      setMembers(membersRes.results || []);
      // Load all services from first batch or all available
      // For now, we'll need to modify the API to load all services
      // Temporary: load from a batch or implement getAll
      setServices([]);
    } catch (error) {
      enqueueSnackbar('Failed to load data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedMember('');
    setSelectedServiceType('');
    setStartDate(dayjs());
    setEndDate(null);
    setDescription('');
    setNotes('');
    setEditingStatus('ACTIVE');
    setEditingService(null);
  };

  const handleSaveService = async () => {
    if (!selectedMember || !selectedServiceType || !startDate) {
      enqueueSnackbar('Please fill in all required fields', { variant: 'warning' });
      return;
    }

    try {
      const input: CongregationServiceInput = {
        serviceTypeId: parseInt(selectedServiceType),
        memberId: parseInt(selectedMember),
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate ? endDate.format('YYYY-MM-DD') : undefined,
        description: description || undefined,
        notes: notes || undefined,
        status: editingStatus,
      };

      if (editingService) {
        await updateService(editingService.serviceId, input);
        enqueueSnackbar('Service updated successfully', { variant: 'success' });
      } else {
        await createService(input);
        enqueueSnackbar('Service assigned successfully', { variant: 'success' });
      }

      setShowDialog(false);
      resetForm();
      await loadData();
    } catch (error) {
      enqueueSnackbar('Failed to save service', { variant: 'error' });
    }
  };

  const handleDeleteService = async (serviceId: number) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      try {
        await deleteService(serviceId);
        enqueueSnackbar('Service deleted successfully', { variant: 'success' });
        await loadData();
      } catch (error) {
        enqueueSnackbar('Failed to delete service', { variant: 'error' });
      }
    }
  };

  const handleEditService = (service: CongregationService) => {
    setEditingService(service);
    setSelectedMember(service.memberId.toString());
    setSelectedServiceType(service.serviceTypeId.toString());
    setStartDate(dayjs(service.startDate));
    setEndDate(service.endDate ? dayjs(service.endDate) : null);
    setDescription(service.description || '');
    setNotes(service.notes || '');
    setEditingStatus(service.status);
    setShowDialog(true);
  };

  const filteredServices = services.filter((service) => {
    const matchesStatus = statusFilter === 'ALL' || service.status === statusFilter;
    const matchesSearch =
      searchQuery === '' ||
      (service.memberName?.toLowerCase().includes(searchQuery.toLowerCase()) || false) ||
      service.serviceType.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const getStatusIcon = (status: CongregationServiceStatus) => {
    switch (status) {
      case 'ACTIVE':
        return <CheckCircleIcon sx={{ color: 'success.500' }} />;
      case 'COMPLETED':
        return <CheckCircleIcon sx={{ color: 'success.300' }} />;
      case 'CANCELLED':
        return <CancelIcon sx={{ color: 'danger.500' }} />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: CongregationServiceStatus) => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'COMPLETED':
        return 'neutral';
      case 'CANCELLED':
        return 'danger';
      default:
        return 'neutral';
    }
  };

  const activeCount = services.filter((s) => s.status === 'ACTIVE').length;
  const completedCount = services.filter((s) => s.status === 'COMPLETED').length;
  const cancelledCount = services.filter((s) => s.status === 'CANCELLED').length;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack spacing={3}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography level="h2">Services Management</Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              Manage all congregation services
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              color="neutral"
              startDecorator={<RefreshIcon />}
              onClick={loadData}
              loading={loading}
            >
              Refresh
            </Button>
            <Button
              variant="soft"
              color="primary"
              startDecorator={<AddIcon />}
              onClick={() => {
                resetForm();
                setShowDialog(true);
              }}
            >
              Assign Service
            </Button>
          </Stack>
        </Stack>

        {/* Stats */}
        <Grid container spacing={2}>
          <Grid xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <Stack>
                <Typography level="h3">{services.length}</Typography>
                <Typography level="body-sm">Total Services</Typography>
              </Stack>
            </Card>
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <Stack>
                <Typography level="h3" sx={{ color: 'success.500' }}>{activeCount}</Typography>
                <Typography level="body-sm">Active</Typography>
              </Stack>
            </Card>
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <Stack>
                <Typography level="h3">{completedCount}</Typography>
                <Typography level="body-sm">Completed</Typography>
              </Stack>
            </Card>
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <Stack>
                <Typography level="h3" sx={{ color: 'danger.500' }}>{cancelledCount}</Typography>
                <Typography level="body-sm">Cancelled</Typography>
              </Stack>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Stack spacing={2}>
          <Stack direction="row" spacing={1}>
            {(['ALL', 'ACTIVE', 'COMPLETED', 'CANCELLED'] as const).map((status) => (
              <Chip
                key={status}
                variant={statusFilter === status ? 'solid' : 'outlined'}
                onClick={() => setStatusFilter(status)}
                color={statusFilter === status ? 'primary' : 'neutral'}
              >
                {status}
              </Chip>
            ))}
          </Stack>

          <Input
            placeholder="Search by member or service type..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </Stack>

        {/* Content */}
        {loading ? (
          <Box sx={{ p: 3 }}>
            <LinearProgress />
          </Box>
        ) : filteredServices.length === 0 ? (
          <Card variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
            <Typography level="body-lg" sx={{ color: 'text.secondary' }}>
              {services.length === 0 ? 'No services assigned' : 'No services match your filters'}
            </Typography>
          </Card>
        ) : (
          <Sheet sx={{ overflow: 'auto', borderRadius: 'md' }}>
            <Table stickyHeader>
              <thead>
                <tr>
                  <th style={{ width: '15%' }}>Member</th>
                  <th style={{ width: '15%' }}>Service Type</th>
                  <th style={{ width: '10%' }}>Status</th>
                  <th style={{ width: '12%' }}>Start Date</th>
                  <th style={{ width: '12%' }}>End Date</th>
                  <th style={{ width: '18%' }}>Description</th>
                  <th style={{ width: '10%' }}>Assigned By</th>
                  <th style={{ width: '8%', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredServices.map((service) => (
                  <tr key={service.serviceId}>
                    <td>
                      <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                        {service.memberName}
                      </Typography>
                    </td>
                    <td>
                      <Typography level="body-sm">{service.serviceType}</Typography>
                    </td>
                    <td>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {getStatusIcon(service.status)}
                        <Chip size="sm" variant="soft" color={getStatusColor(service.status)}>
                          {service.status}
                        </Chip>
                      </Stack>
                    </td>
                    <td>
                      <Typography level="body-sm">
                        {dayjs(service.startDate).format('MMM DD, YYYY')}
                      </Typography>
                    </td>
                    <td>
                      <Typography level="body-sm">
                        {service.endDate ? dayjs(service.endDate).format('MMM DD, YYYY') : '-'}
                      </Typography>
                    </td>
                    <td>
                      <Typography
                        level="body-sm"
                        sx={{
                          maxWidth: '150px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {service.description || '-'}
                      </Typography>
                    </td>
                    <td>
                      <Typography level="body-sm">{service.assignedByName}</Typography>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Button
                          size="sm"
                          variant="plain"
                          color="neutral"
                          onClick={() => handleEditService(service)}
                        >
                          <EditIcon />
                        </Button>
                        <Button
                          size="sm"
                          variant="plain"
                          color="danger"
                          onClick={() => handleDeleteService(service.serviceId)}
                        >
                          <DeleteIcon />
                        </Button>
                      </Stack>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Sheet>
        )}

        {/* Service Assignment Dialog */}
        <Modal
          aria-labelledby="modal-title"
          open={showDialog}
          onClose={() => setShowDialog(false)}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: { xs: 1.5, sm: 2 },
            overflow: 'auto',
          }}
        >
          <ModalDialog
            variant="outlined"
            sx={{
              width: '100%',
              maxWidth: 520,
              borderRadius: 'md',
              p: { xs: 2, sm: 3 },
              boxShadow: 'lg',
              maxHeight: '90vh',
              overflow: 'auto',
            }}
          >
            <ModalClose variant="plain" sx={{ m: 1 }} />
            <Typography
              level="h4"
              sx={{
                mb: 2,
                textAlign: 'left',
              }}
            >
              {editingService ? 'Edit Service' : 'Assign Service'}
            </Typography>
            <Stack spacing={2}>
              <FormControl>
                <FormLabel>Member *</FormLabel>
                <Select
                  value={selectedMember}
                  onChange={(_, value) => setSelectedMember(value || '')}
                  placeholder="Select a member"
                >
                  {members.map((member) => (
                    <Option key={member.member.id} value={member.member.id.toString()}>
                      {member.member.displayName}
                    </Option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Service Type *</FormLabel>
                <Select
                  value={selectedServiceType}
                  onChange={(_, value) => setSelectedServiceType(value || '')}
                >
                  {serviceTypes.map((type) => (
                    <Option key={type.id} value={type.id.toString()}>
                      {type.name}
                    </Option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Start Date *</FormLabel>
                <DatePicker 
                  value={startDate} 
                  onChange={(date) => setStartDate(date)}
                  slotProps={{
                    textField: {
                      sx: {
                        '& input': {
                          color: '#fff !important',
                        },
                        '& .MuiOutlinedInput-root': {
                          color: '#fff !important',
                        },
                        '& .MuiOutlinedInput-input': {
                          color: '#fff !important',
                        },
                        '& .MuiIconButton-root': {
                          color: '#fff !important',
                        },
                        '& svg': {
                          color: '#fff !important',
                        }
                      }
                    }
                  }}
                />
              </FormControl>

              <FormControl>
                <FormLabel>End Date</FormLabel>
                <DatePicker 
                  value={endDate} 
                  onChange={(date) => setEndDate(date)}
                  slotProps={{
                    textField: {
                      sx: {
                        '& input': {
                          color: '#fff !important',
                        },
                        '& .MuiOutlinedInput-root': {
                          color: '#fff !important',
                        },
                        '& .MuiOutlinedInput-input': {
                          color: '#fff !important',
                        },
                        '& .MuiIconButton-root': {
                          color: '#fff !important',
                        },
                        '& svg': {
                          color: '#fff !important',
                        }
                      }
                    }
                  }}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Status</FormLabel>
                <Select
                  value={editingStatus}
                  onChange={(_, value) => setEditingStatus(value as CongregationServiceStatus)}
                >
                  <Option value="ACTIVE">Active</Option>
                  <Option value="COMPLETED">Completed</Option>
                  <Option value="CANCELLED">Cancelled</Option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Service description"
                  minRows={2}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Notes</FormLabel>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Additional notes"
                  minRows={2}
                />
              </FormControl>

              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  color="neutral"
                  onClick={() => setShowDialog(false)}
                >
                  Cancel
                </Button>
                <Button variant="soft" color="primary" onClick={handleSaveService}>
                  {editingService ? 'Update' : 'Assign'}
                </Button>
              </Stack>
            </Stack>
          </ModalDialog>
        </Modal>
      </Stack>
    </LocalizationProvider>
  );
}
